import React, { useEffect, useMemo, useState } from "react";
import StickyHeader from "../../../components/ModuleCommon/StickyHeader/StickyHeader";
import { carrierSourcingApi } from "./api/carrierSourcingApi";
import { apiErrorMessage, mapPipelineRow } from "./mappers";
import { EQUIPMENT_TYPES, RATE_TYPES } from "./mockData";
import { RiskChip } from "./ui";

const fieldClass =
  "border rounded-md p-2 w-full bg-white dark:bg-acs-dark-surface dark:border-acs-dark-border dark:text-acs-dark-text";

function toggleInList(list, value) {
  const has = list.includes(value);
  return has ? list.filter((x) => x !== value) : [...list, value];
}

export default function NewRateRequest({
  onCancel,
  onSend,
  initialCarrierId = null,
  initialCarrierName = null,
}) {
  const lockedCarrier = Boolean(initialCarrierId);
  const [search, setSearch] = useState("");
  const [carriers, setCarriers] = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [selectedId, setSelectedId] = useState(
    initialCarrierId != null ? String(initialCarrierId) : null
  );
  const [lockedName, setLockedName] = useState(initialCarrierName || "");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    origin: "",
    destination: "",
    equipment: ["Dry Van"],
    rateTypes: ["New lane"],
    askRate: "",
    fuelTerms: "",
    message: "",
  });
  const [error, setError] = useState("");

  useEffect(() => {
    if (lockedCarrier) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoadingCarriers(true);
      try {
        const res = await carrierSourcingApi.getPipeline({
          search: search.trim() || undefined,
          page: 0,
          size: 20,
        });
        if (cancelled) return;
        const content = res.data?.content || [];
        setCarriers(content.map(mapPipelineRow));
      } catch (err) {
        if (!cancelled) {
          setCarriers([]);
          setError(apiErrorMessage(err, "Failed to search carriers"));
        }
      } finally {
        if (!cancelled) setLoadingCarriers(false);
      }
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [search, lockedCarrier]);

  useEffect(() => {
    if (!lockedCarrier || !initialCarrierId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await carrierSourcingApi.getCarrier(Number(initialCarrierId));
        if (cancelled) return;
        const name = res.data?.legalName || initialCarrierName || "Carrier";
        setLockedName(name);
        setSelectedId(String(initialCarrierId));
      } catch {
        if (!cancelled && initialCarrierName) {
          setLockedName(initialCarrierName);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [lockedCarrier, initialCarrierId, initialCarrierName]);

  const selected = useMemo(
    () => carriers.find((c) => c.id === selectedId) || null,
    [carriers, selectedId]
  );

  const carrierLabel = lockedCarrier
    ? lockedName || initialCarrierName || "Carrier"
    : selected?.name || "Not selected";

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSend = async () => {
    if (!selectedId) {
      setError("Select an existing carrier first.");
      return;
    }
    if (!form.origin.trim() || !form.destination.trim()) {
      setError("Origin and destination are required.");
      return;
    }
    if (!(form.equipment || []).length) {
      setError("Select at least one equipment type.");
      return;
    }
    if (!(form.rateTypes || []).length) {
      setError("Select at least one rate type.");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await onSend({ carrierId: selectedId, ...form });
    } catch {
      /* parent notifies */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="sticky top-0 z-10 w-full bg-white shadow-md dark:bg-acs-dark-surface">
        <StickyHeader
          mode={null}
          infoData={{
            Carrier: {
              label: "Carrier",
              value: carrierLabel,
              readonly: true,
            },
            Stage: {
              label: "Creates",
              value: "Draft rate version",
              readonly: true,
            },
          }}
          buttons={[
            { label: "Cancel", type: "secondary", onClick: onCancel },
            {
              label: saving ? "Sending…" : "Send Rate Request",
              type: "primary",
              onClick: handleSend,
            },
          ]}
        />
      </div>

      <div className="min-h-0 w-full flex-1 overflow-y-auto bg-white p-fluid-1 dark:bg-acs-dark-surface">
        <div className="rounded-[clamp(0.75rem,1vw,1.5rem)] bg-white p-fluid-2 shadow-fluid-card dark:bg-acs-dark-surface">
          <h2 className="mb-fluid-2 text-fluid-3xl font-bold text-gray-900 dark:text-acs-dark-text">
            Add Rate{" "}
            {lockedCarrier ? (
              <span className="text-fluid-base font-normal text-gray-500">
                - {carrierLabel}
              </span>
            ) : null}
          </h2>

          {error ? (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-fluid-sm text-red-700">
              {error}
            </div>
          ) : null}

          {!lockedCarrier ? (
            <>
              <h3 className="mb-fluid-2 text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
                Select Existing Carrier
              </h3>
              <div className="mb-6">
                <input
                  className={`${fieldClass} mb-3`}
                  placeholder="Search carriers by name, MC, or DOT number..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <div className="space-y-2">
                  {loadingCarriers ? (
                    <p className="text-fluid-sm text-gray-500">Searching…</p>
                  ) : null}
                  {carriers.map((c) => {
                    const isSelected = c.id === selectedId;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => setSelectedId(c.id)}
                        className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left transition-none ${
                          isSelected
                            ? "border-[#223688] bg-[#EEF1FF]"
                            : "border-gray-300 bg-white hover:border-[#223688] dark:border-acs-dark-border dark:bg-acs-dark-surface"
                        }`}
                      >
                        <div>
                          <div className="text-fluid-sm font-semibold">{c.name}</div>
                          <div className="font-mono text-dense-xs text-gray-500">
                            {c.mc} · {c.dot} · {(c.equipment || []).join(", ")}
                          </div>
                        </div>
                        <RiskChip risk={c.risk || "Low"} />
                      </button>
                    );
                  })}
                  {!loadingCarriers && !carriers.length ? (
                    <p className="text-fluid-sm text-gray-500">
                      No carriers available. Add a carrier first.
                    </p>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}

          <h3 className="mb-fluid-2 text-fluid-lg font-semibold text-gray-900 dark:text-acs-dark-text">
            Lane &amp; Rate Ask
          </h3>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <div>
              <label className="mb-1 block font-medium">Origin</label>
              <input
                className={fieldClass}
                value={form.origin}
                onChange={(e) => set("origin", e.target.value)}
                placeholder="e.g. Charlotte, NC"
              />
            </div>
            <div>
              <label className="mb-1 block font-medium">Destination</label>
              <input
                className={fieldClass}
                value={form.destination}
                onChange={(e) => set("destination", e.target.value)}
                placeholder="e.g. Atlanta, GA"
              />
            </div>
            <div>
              <label className="mb-1 block font-medium">Ask rate</label>
              <input
                className={fieldClass}
                value={form.askRate}
                onChange={(e) => set("askRate", e.target.value)}
                placeholder="e.g. 1850"
              />
            </div>
            <div className="md:col-span-3">
              <label className="mb-1 block font-medium">Equipment</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {EQUIPMENT_TYPES.map((eq) => (
                  <label key={eq} className="flex items-center gap-2 text-fluid-sm">
                    <input
                      type="checkbox"
                      checked={form.equipment.includes(eq)}
                      onChange={() =>
                        set("equipment", toggleInList(form.equipment, eq))
                      }
                    />
                    {eq}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-3">
              <label className="mb-1 block font-medium">Rate type</label>
              <div className="mt-2 flex flex-wrap gap-3">
                {RATE_TYPES.map((rt) => (
                  <label key={rt} className="flex items-center gap-2 text-fluid-sm">
                    <input
                      type="checkbox"
                      checked={form.rateTypes.includes(rt)}
                      onChange={() =>
                        set("rateTypes", toggleInList(form.rateTypes, rt))
                      }
                    />
                    {rt}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block font-medium">Fuel terms</label>
              <input
                className={fieldClass}
                value={form.fuelTerms}
                onChange={(e) => set("fuelTerms", e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block font-medium">Message</label>
              <textarea
                rows={3}
                className={fieldClass}
                value={form.message}
                onChange={(e) => set("message", e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
